import React from 'react';

// Material-UI
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { amber } from '@mui/material/colors';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { useSnackbar } from 'notistack';

import Waiver from './Waiver';

// Styles
import { useTheme } from '@mui/material/styles';

import {
  HTTPHeaders,
  MemberWaiverStatus
} from '../interfaces';

import {
  API_URL,
  WaiverStatus
} from '../global';

// Redux
import { useAppSelector } from '../hooks/UseReduxState'
import { getFiscalYear } from '../../redux/requestSlice'

// Auth0 user hooks
import { useAuth0User } from '../hooks/UseAuth0User';

interface WaiverDialogProps {
  handleClose: () => void;
  memberWaivers: Array<MemberWaiverStatus>;
  show: boolean;
  handleWaiverSigned: () => void;
}

const WaiverDialog: React.FC<WaiverDialogProps> = (props) => {

  const { getAccessToken, memberID } = useAuth0User();
  const { enqueueSnackbar } = useSnackbar();

  const [accepted, setAccepted] = React.useState<boolean>(false);

  // Redux
  const fiscalYear: number = useAppSelector(getFiscalYear);

  // #region Styles

  const theme = useTheme();
  const styles = {
    buttonContainer: {
      padding: theme.spacing(0, 0, 1, 0)
    },
    divider: {
      padding: theme.spacing(2, 0, 0, 0)
    },
    instructions: {
      padding: theme.spacing(2, 0, 0, 0)
    },
    membersContainer: {
      padding: theme.spacing(2, 0, 2, 0)
    },
    noteContainer: {
      padding: theme.spacing(2, 0, 0, 0)
    },
    title: {
      backgroundColor: amber[300]
    },
    waiver: {
      padding: theme.spacing(2, 0, 0, 0)
    },
  };

  // #endregion

  // #region Methods

  /*
* Sign member waiver
*/
  const sign = async (id: number) => {

    //setSaveButtonEnabled(false);

    // Show dialog w/ spinner
    //setSaveInProgress(true);

    // Auth0 API token
    const accessToken = await getAccessToken();

    const headers: HTTPHeaders = {};
    headers['Content-Type'] = 'application/json';
    headers['Accept'] = 'application/json';
    headers['Authorization'] = `Bearer ${accessToken}`;

    const m = memberID();
    const params = new URLSearchParams();
    params.append('id', id.toString());
    params.append('userID', m.toString());

    const url = new URL(`${API_URL}/v1/members/waiver`);
    url.search = params.toString();

    await fetch(url.toString(), {
      method: 'POST',
      body: JSON.stringify({ id: id, userID: m }),
      headers: headers,
      mode: 'cors'
    })
      .then((response) => {
        //setSaveInProgress(false);
        props.handleWaiverSigned();
        if (response.ok) {
          setAccepted(false);
          enqueueSnackbar(`Member waiver signed`, { variant: 'success' });
        } else {
          enqueueSnackbar('Member waiver signing unsuccessful. Please report using the Feedback form.', { variant: 'error' });
        }
      })
      .catch(error => {
        //setSaveInProgress(false);
        enqueueSnackbar('Member waiver signing unsuccessful. Please report using the Feedback form.', { variant: 'error' });
      })
  }

  // #endregion

  // #region Event Handlers

  /*
   * Handle waive acceptance
   */
  const handleAccept = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAccepted(event.target.checked);
  };

  // #endregion

  return (
    <Dialog
      open={props.show}
      onClose={props.handleClose}
      fullWidth={true}
      maxWidth="md"
    >
      <DialogTitle sx={styles.title}>Member Waiver Status</DialogTitle>
      <DialogContent>
        <Grid container sx={styles.waiver}>
          <Waiver
            accepted={accepted}
            handleAccept={handleAccept}
          />
        </Grid>
        <Divider sx={styles.divider} />
        <Typography variant="body2" gutterBottom sx={styles.instructions}>Read the waiver and check the acknowledgement at the bottom, then Accept. Each adult member (18+) needs to login and sign the FY{fiscalYear} waiver (Valid until FY{fiscalYear + 3}) before using the lodge.</Typography>
        <Box sx={styles.membersContainer}>
          {props.memberWaivers.map(m => (
            <Grid container key={m.memberID} alignItems="center" spacing={1} sx={styles.buttonContainer}>
              <Grid size={{ sm: 6, lg: 4 }}>
                <Typography variant="body2">{`${m.memberName} (${m.status})`}</Typography>
              </Grid>
              <Grid>
                <Typography variant="body2"><b>{`${m.waiverStatus}`}</b></Typography>
              </Grid>
              {(m.memberID === memberID() && (m.waiverStatusID === WaiverStatus.Pending)) &&
                <Grid>
                  <Button disabled={!accepted} variant="contained" size="small" onClick={() => sign(m.memberID)}>Accept</Button>
                </Grid>
              }
            </Grid>
          ))}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={() => props.handleClose()} color="primary">Close</Button>
      </DialogActions>
    </Dialog>
  );

}

export default WaiverDialog;